/**
 * Retired-service rules.
 *
 * `services.is_active` lets a service be taken out of circulation without being
 * deleted — deleting is not actually possible, because
 * `service_request_services.service_id` and `visit_services.service_id` are FKs
 * with no cascade, so removing a service that has ever been used fails.
 *
 * The rule that matters, and the one easy to get wrong: an inactive service must
 * stop appearing when authoring **new** work, but must still *display* wherever
 * it is already attached. Otherwise a request that legitimately references a
 * since-retired service renders with a blank line item.
 *
 * These helpers are deliberately typed on a minimal shape rather than a generated
 * client type: `ServiceDb` is imported from several generated clients
 * (`services_api`, `caregiver_api`, …) and not all are regenerated at the same
 * time, so the field may be absent from some of them.
 */

/** The only field these rules care about. */
export type HasActivity = { isActive?: boolean | null };

/**
 * Whether a service may be offered for new selection.
 *
 * **Fails open.** Only an explicit `false` retires a service; `undefined` and
 * `null` both read as active. That matters because a generated API client that
 * has not been regenerated since the column shipped simply has no `isActive`
 * field — and treating "missing" as inactive would blank out every picker in the
 * app rather than degrading quietly.
 */
export const isSelectableService = (service: HasActivity): boolean => service.isActive !== false;

/**
 * Drop retired services from a list offered for new selection.
 *
 * Use on the *search/fetch* results of a picker. Do NOT use on the values a
 * record already holds — already-attached services must keep rendering.
 */
export const excludeRetiredServices = <T extends object>(services: readonly T[]): T[] =>
  // Constrained to `object` rather than `HasActivity` on purpose. `HasActivity` is
  // an all-optional ("weak") type, so TypeScript rejects any argument that shares
  // none of its properties — which is exactly the case this must support: a
  // generated client predating the column has no `isActive` field at all.
  services.filter((service) => isSelectableService(service as HasActivity));
